import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, Box, InputBase, List, ListItemButton, ListItemText, ListItemIcon, Typography,
  Divider, Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import pageRegistry from '../../navigation/pageRegistry';
import { getRecentPages } from '../../hooks/useRecentPages';
import authService from '../../services/authService';

// RC18.2 Phase 3 — the enterprise Command Palette (Ctrl+K / Cmd+K): jump to any permitted
// page by name, with recently-visited pages surfaced first on an empty query. This is
// deliberately separate from `GlobalSearchBar` (entity search — candidates/jobs/etc.):
// the palette owns the Ctrl+K shortcut app-wide; `GlobalSearchBar` remains a click-to-focus
// field in the AppBar for entity lookup, matching the "search vs. navigate" split used by
// every enterprise SaaS product this sprint's brief names (Linear, GitHub, Notion, etc.).
const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const permittedPages = useMemo(
    () => pageRegistry.filter((p) => {
      if (!p.permission) return true;
      return Array.isArray(p.permission)
        ? authService.hasAnyPermission(...p.permission)
        : authService.hasPermission(p.permission);
    }),
    []
  );

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      const recent = getRecentPages();
      return recent.length > 0
        ? recent.map((r) => ({ ...r, recent: true }))
        : permittedPages.slice(0, 8);
    }
    return permittedPages.filter((p) => p.label.toLowerCase().includes(q));
  }, [query, permittedPages]);

  const goTo = useCallback((path) => {
    setOpen(false);
    navigate(path);
  }, [navigate]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      const target = results[activeIndex];
      if (target) goTo(target.path);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { position: 'fixed', top: 96, m: 0, borderRadius: 3 } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <SearchIcon sx={{ color: 'text.secondary', mr: 1.5 }} />
        <InputBase
          inputRef={inputRef}
          fullWidth
          placeholder="Jump to a page…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
          onKeyDown={handleKeyDown}
          inputProps={{ 'aria-label': 'command palette' }}
        />
        <Chip label="Esc" size="small" variant="outlined" sx={{ fontSize: 11 }} />
      </Box>
      <List dense sx={{ maxHeight: 400, overflowY: 'auto', py: 1 }}>
        {query.trim() === '' && (
          <Typography variant="caption" sx={{ px: 2, pb: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {results[0]?.recent ? 'Recently visited' : 'Pages'}
          </Typography>
        )}
        {results.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2, textAlign: 'center' }}>
            No matching pages.
          </Typography>
        )}
        {results.map((item, i) => (
          <React.Fragment key={item.path}>
            {i > 0 && query.trim() === '' && results[i - 1]?.recent && !item.recent && <Divider sx={{ my: 0.5 }} />}
            <ListItemButton
              selected={i === activeIndex}
              onMouseEnter={() => setActiveIndex(i)}
              onClick={() => goTo(item.path)}
              sx={{ mx: 1, borderRadius: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {item.recent ? <HistoryIcon fontSize="small" /> : <ArrowForwardIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItemButton>
          </React.Fragment>
        ))}
      </List>
    </Dialog>
  );
};

export default CommandPalette;
